import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef} from '@angular/material';
import {
  AssistanceCoverageConfig,
  AssistanceCoveragesComparisonConfig,
  AssistanceCoverageVariantPricing
} from './assistance-coverages-comparison.config';
import {DialogConfigBase} from '../../../../components/dialogs/dialog-config-base';
import {DialogConfigInterface} from '../../../../components/dialogs/dialog-config.interface';
import {
  AssistanceCoverage,
  OfferModificationCode,
  PevnoAssistanceCoverage,
  UniqaAssistanceCoverage
} from '../../../../../core/consts/offer-modification-code.enum';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {
  StickyCloseDialogComponent
} from '../../../../components/dialogs/sticky-close-dialog/sticky-close-dialog.component';
import {DeviceService} from '../../../../services/device.service';
import {
  OfferModificationConfig,
  OfferModificationsConfigService
} from '../../../../../core/services/impl/offer-modifications/offer-modifications-config.service';
import {InsuranceCompanyCode} from '../../../../models/insurance-company-code.enum';

@Component({
  selector: 'app-assistance-coverages-comparison',
  templateUrl: './assistance-coverages-comparison.component.html',
  styleUrls: ['./assistance-coverages-comparison.component.scss']
})
export class AssistanceCoveragesComparisonComponent extends StickyCloseDialogComponent implements OnInit {

  coveragesConfig: AssistanceCoverageConfig[];
  pricingData: AssistanceCoverageVariantPricing[];
  modificationConfig: OfferModificationConfig;
  recommendedVariants: string[] = [];

  assistanceVariantsOrder: AssistanceCoverage[];

  static getConfigBuilder(): AssistanceCoveragesComparisonDialogConfig {
    return new AssistanceCoveragesComparisonDialogConfig();
  }

  constructor(@Inject(MAT_DIALOG_DATA) public data: any,
              public deviceService: DeviceService,
              private configService: OfferModificationsConfigService,
              private dialog: MatDialogRef<AssistanceCoveragesComparisonComponent>) {
    super(deviceService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.createConfig();
    this.sortPricingData();
    this.initRecommendedVariants();
  }

  close() {
    this.dialog.close();
  }

  selectAssistance(name: AssistanceCoverage): void {
    this.dialog.close(name);
  }

  createConfig(): void {
    const config = new AssistanceCoveragesComparisonConfig(this.configService.insuranceCompanyCode);
    this.coveragesConfig = config.createComparisonConfig();
    this.assistanceVariantsOrder = config.getAssistanceCoveragesForComparison();
  }

  getCoverageLabel(config: AssistanceCoverageConfig): string {
    if (isNotNullOrUndefined(config.label)) {
      return config.label;
    }
    return `ASSISTANCE_COVERAGES_COMPARISON.coveragesLabels.${config.coverageName}`;
  }

  hasVariant(variantName: string): boolean {
    return !!this.data.find((pricingItem: AssistanceCoverageVariantPricing) => pricingItem.name === variantName);
  }

  isVariantRecommended(variantName: string): boolean {
    return this.recommendedVariants.includes(variantName);
  }

  shouldShowRecommendation(variantName: string, index: number): boolean {
    return index === 0 && this.isVariantRecommended(variantName);
  }

  getVariantConfig(config: AssistanceCoverageConfig, variant: AssistanceCoverage): string | boolean {
    const variantValue = config.values.find(item => item.coverage === variant);
    return variantValue ? variantValue.value : null;
  }

  private sortPricingData(): void {
    const data = [];
    this.assistanceVariantsOrder.forEach(name => {
      const item = this.data.find((pricingItem: AssistanceCoverageVariantPricing) => pricingItem.name === name);
      if (isNotNullOrUndefined(item)) {
        data.push(item);
      }
    });
    this.pricingData = data;
  }

  private initRecommendedVariants(): void {
    this.modificationConfig = this.configService.getModificationConfig(OfferModificationCode.ASSISTANCE);
    if (this.modificationConfig.hasOwnProperty('isOptionRecommended')) {
      let allVariants = [];
      if (this.configService.insuranceCompanyCode === InsuranceCompanyCode.UNIQA) {
        allVariants = Object.values(UniqaAssistanceCoverage);
      } else if (this.configService.insuranceCompanyCode === InsuranceCompanyCode.PEVNO) {
        allVariants = Object.values(PevnoAssistanceCoverage);
      }
      this.recommendedVariants = allVariants.filter(variant => this.modificationConfig.isOptionRecommended(variant));
    }
  }

}

export class AssistanceCoveragesComparisonDialogConfig extends DialogConfigBase implements DialogConfigInterface {
  private config: MatDialogConfig = {
    disableClose: true,
    panelClass: ['app-offers-dialog', 'offer-modifications-comparison-dialog-panel']
  };

  constructor() {
    super();
  }

  buildConfiguration(data: AssistanceCoverageVariantPricing[]): MatDialogConfig {
    return this.buildConfig(data, this.config);
  }
}
